// Article feature types — adapted to the real backend (S9).
//
// Backend `BaseStatus` enum is numeric:
//   Inactive=0, Active=1, Deleted=2, Archived=3, Draft=4, InReview=5,
//   Approved=6, Published=7, Rejected=8
//
// For the FE we expose a string union for granular workflow handling and a
// helper `toContentStatus()` that maps onto the existing 4-state `ContentStatus`
// used by `StatusBadge` in @shared/constants/statuses.

import { useTranslation } from 'react-i18next';
import type { ContentStatus } from '@shared/constants/statuses';

export type ArticleStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'REJECTED'
  | 'DELETED';

/** Statuses surfaced in the list-page status filter. */
export const ARTICLE_FILTER_STATUSES: ArticleStatus[] = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'PUBLISHED',
  'ARCHIVED',
  'REJECTED',
];

/** Resolves the granular article status label via the `articles` namespace
 *  (status.*). Mirrors the shared `useContentStatusLabel` hook pattern. */
export function useArticleStatusLabel(): (status: ArticleStatus) => string {
  const { t } = useTranslation(['articles', 'common', 'validation', 'errors']);
  const td = t as unknown as (key: string) => string;
  return (status) => td(`status.${status}`);
}

const STATUS_TO_NUM: Record<ArticleStatus, number> = {
  DELETED: 2,
  ARCHIVED: 3,
  DRAFT: 4,
  IN_REVIEW: 5,
  APPROVED: 6,
  PUBLISHED: 7,
  REJECTED: 8,
};

const NUM_TO_STATUS: Record<number, ArticleStatus> = {
  2: 'DELETED',
  3: 'ARCHIVED',
  4: 'DRAFT',
  5: 'IN_REVIEW',
  6: 'APPROVED',
  7: 'PUBLISHED',
  8: 'REJECTED',
};

export function articleStatusFromNumber(n: number): ArticleStatus {
  return NUM_TO_STATUS[n] ?? 'DRAFT';
}

export function articleStatusToNumber(s: ArticleStatus): number {
  return STATUS_TO_NUM[s];
}

/** Map the granular article status onto the 4-state `ContentStatus` used by
 *  the shared `StatusBadge` (which we are not allowed to modify). */
export function toContentStatus(s: ArticleStatus): ContentStatus {
  switch (s) {
    case 'DRAFT':
    case 'REJECTED':
      return 'DRAFT';
    case 'IN_REVIEW':
    case 'APPROVED':
      return 'REVIEW';
    case 'PUBLISHED':
      return 'PUBLISHED';
    case 'ARCHIVED':
    case 'DELETED':
      return 'ARCHIVED';
  }
}

/** Full single-article record (matches BE `ArticleDto`). */
export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  categoryId: string | null;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  /** MOD-08 — comma-separated SEO keywords (additive; null on legacy rows). */
  keywords: string | null;
  allowIndexing: boolean;
  isFeatured: boolean;
  featuredOrder: number | null;
  viewCount: number;
  status: ArticleStatus;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tagIds: string[];
  // --- İçerik i18n (MOD-09) ---
  languageId: string;
  /** Çeviri grubu — aynı groupId'yi paylaşan içerikler birbirinin varyantıdır. */
  groupId: string;
  /** Bu içeriğin grubunda mevcut dil KODLARI (örn. ["tr","en"]). */
  variantLanguageCodes: string[];
}

/** Light row used by the list endpoint (matches BE `ArticleListItemDto`). */
export interface ArticleListItem {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  categoryId: string | null;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  isFeatured: boolean;
  featuredOrder: number | null;
  viewCount: number;
  status: ArticleStatus;
  publishedAt: string | null;
  updatedAt: string;
  languageId: string;
  groupId: string;
  variantLanguageCodes: string[];
}

export interface ArticleVersion {
  id: string;
  articleId: string;
  versionNumber: number;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  categoryId: string | null;
  authorId: string | null;
  coverImageUrl: string | null;
  readingTime: number;
  metaTitle: string | null;
  metaDescription: string | null;
  canonicalTag: string | null;
  customCode: string | null;
  allowIndexing: boolean;
  tagIds: string[];
  authorUserId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

/**
 * Legacy 4-state status union still referenced by older callers (e.g. the
 * Dashboard widgets). We translate it onto the granular BE-aware enum
 * inside the API client.
 */
export type ArticleStatusLegacy = 'DRAFT' | 'REVIEW' | 'PUBLISHED' | 'ARCHIVED';

export interface ArticleListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ArticleStatus | ArticleStatusLegacy;
  categoryId?: string;
  authorId?: string;
  tagId?: string;
  featuredOnly?: boolean;
  publishedOnly?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  descending?: boolean;
  /** İçerik i18n — belirli bir dile göre süz. */
  languageId?: string;
}

export function normalizeArticleStatus(
  s: ArticleStatus | ArticleStatusLegacy,
): ArticleStatus {
  if (s === 'REVIEW') return 'IN_REVIEW';
  return s;
}

/** Body for POST /articles and PUT /articles/{id}. */
export interface ArticleWriteInput {
  title: string;
  body: string;
  categoryId: string;
  slug?: string | null;
  excerpt?: string | null;
  authorId?: string | null;
  coverImageUrl?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  canonicalTag?: string | null;
  customCode?: string | null;
  /** MOD-08 — comma-separated SEO keywords (max 500 chars). */
  keywords?: string | null;
  allowIndexing?: boolean;
  tagIds?: string[] | null;
  changeNote?: string | null;
  // --- İçerik i18n (create) — languageId yoksa BE varsayılan dili kullanır;
  // groupId yalnız varyant eklerken (mevcut gruba yeni dil) gönderilir. ---
  languageId?: string | null;
  groupId?: string | null;
}

export type BulkArticleAction =
  | 'publish'
  | 'unpublish'
  | 'archive'
  | 'delete'
  | 'feature'
  | 'unfeature';

const BULK_ACTION_NUM: Record<BulkArticleAction, number> = {
  publish: 1,
  unpublish: 2,
  archive: 3,
  delete: 4,
  feature: 5,
  unfeature: 6,
};

export function bulkActionToNumber(a: BulkArticleAction): number {
  return BULK_ACTION_NUM[a];
}

export interface ReorderFeaturedItem {
  id: string;
  /** New zero-based featured order. */
  featuredOrder: number;
  /** Base64-encoded `byte[]` rowVersion for optimistic concurrency. Optional —
   *  when omitted the BE skips the concurrency check. */
  rowVersion?: string | null;
}

// ---- Activity (FE-only fallback) -------------------------------------------
// The BE does not yet expose a per-article activity feed; the activity panel
// keeps reading from the local audit log via `recordAuditAction`.
export interface ArticleActivityEntry {
  id: string;
  at: string;
  actor: string;
  action: string;
  summary: string;
}
